import React, { useMemo } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { LoadingGIF } from '@components/basics/LoadingGif';
import { Button } from '@components/main-components/button/Button';
import { ParamsStore } from '@context/params';
import { type AudienceSongInfoFragment } from '@graphql/reactQuery';
import type { TierNft } from '@graphql/reactQuery';
import { TAB_CHOICES } from '../Activity/store';
import { LoadingSeats } from './Seat';
import type { AudienceSortType } from './constants';
import { useAudience } from './useAudience';

export function AudienceSeats({
  releaseFrag,
  tier,
  sort,
  paginate,
  tabsTopOffset,
}: {
  releaseFrag: AudienceSongInfoFragment;
  tier: TierNft;
  sort: AudienceSortType;
  paginate: boolean;
  tabsTopOffset: number | undefined;
}) {
  const { collectors, hasNextPage, isFetchingNextPage, loading, fetchMore, rows } = useAudience({
    releaseId: releaseFrag.id,
    tier,
    sort,
    paginate,
  });

  const loadingNext = isFetchingNextPage && hasNextPage;

  const footer = useMemo(() => {
    return loadingNext && paginate ? <LoadingGIF className="md:mt-0" /> : null;
  }, [loadingNext, paginate]);

  return (
    <>
      {loading && !collectors.length ? (
        <LoadingSeats />
      ) : (
        // We show 4 rows instead of 1 single grid because of the numbers of columns in each row
        // 4, 6, 7, 8 and 9, least common multiple is a grid of 504 cols, which is a horrible idea
        // we separate in 4 rows + the rest instead
        <div className="flex w-full flex-col gap-2 md:gap-4">
          {rows.firstRow.length ? (
            <div className="grid w-full grid-cols-[repeat(4,1fr)] gap-2 overflow-hidden md:gap-3">
              {rows.firstRow}
            </div>
          ) : null}

          {rows.secondRow.length ? (
            <div className="hidden w-full grid-cols-[repeat(6,1fr)] gap-2 overflow-hidden md:grid md:gap-3">
              {rows.secondRow}
            </div>
          ) : null}

          {rows.thirdRow.length ? (
            <div className="hidden w-full grid-cols-[repeat(7,1fr)] gap-2 overflow-hidden md:grid md:gap-3">
              {rows.thirdRow}
            </div>
          ) : null}

          {rows.fourthRow.length ? (
            <div className="hidden w-full grid-cols-[repeat(8,1fr)] gap-2 overflow-hidden md:grid md:gap-3">
              {rows.fourthRow}
            </div>
          ) : null}

          {/* As all remaining rows have same width/height we can virtualize this grid */}
          {rows.remainingRows.length ? (
            <>
              <VirtuosoGrid
                context={{ fetchMore }}
                endReached={fetchMore}
                data={rows.remainingRows}
                itemContent={(_, item) => item}
                overscan={200}
                listClassName="grid w-full grid-cols-[repeat(6,1fr)] md:grid-cols-[repeat(9,1fr)] gap-2 overflow-hidden md:gap-3"
                components={{ Footer: () => footer }}
                useWindowScroll
              />
              {/* Padding to show loading state */}
              {paginate && loadingNext && <div className="pt-20" />}
            </>
          ) : null}
        </div>
      )}

      {hasNextPage && !paginate && (
        <div className="mt-3 flex w-full items-center justify-center">
          <Button
            label="See All"
            variant="tertiary"
            size="S"
            onClick={() => {
              ParamsStore.song_tab = TAB_CHOICES.AUDIENCE;
              window.scrollTo({ top: tabsTopOffset, behavior: 'instant' });
            }}
          />
        </div>
      )}
    </>
  );
}
